import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import webhookEventService, {
  WEBHOOK_EVENT_SOURCES,
  WEBHOOK_EVENT_STATUSES,
  WEBHOOK_SOURCE_LABELS,
} from '../../services/webhookEventService';
import type {
  WebhookEventDetail,
  WebhookEventQuery,
  WebhookEventSource,
  WebhookEventStats,
  WebhookEventStatus,
  WebhookEventSummary,
} from '../../services/webhookEventService';

const AUTO_REFRESH_MS = 60_000;
const SEARCH_DEBOUNCE_MS = 400;
const PAGE_SIZE_OPTIONS = [25, 50, 100];

// Theme status tokens: green only for "done", amber/red for failures, blue for in-flight.
const STATUS_META: Record<string, { label: string; token: string }> = {
  success: { label: 'Success', token: 'completed' },
  failed: { label: 'Failed', token: 'attention' },
  pending: { label: 'Pending', token: 'onPace' },
  processing: { label: 'Processing', token: 'onPace' },
};

interface Filters {
  source: WebhookEventSource | '';
  status: WebhookEventStatus | '';
  eventType: string;
  search: string;
  from: string;
  to: string;
}

const EMPTY_FILTERS: Filters = { source: '', status: '', eventType: '', search: '', from: '', to: '' };

function StatusCell({ status }: { status: string }) {
  const meta = STATUS_META[status] ?? { label: status, token: 'enrolled' };
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, bgcolor: `status.${meta.token}.dot` }} />
      <Typography variant="body2" sx={{ fontWeight: 600, color: `status.${meta.token}.fg`, whiteSpace: 'nowrap' }}>
        {meta.label}
      </Typography>
    </Box>
  );
}

function sourceLabel(source: string): string {
  return WEBHOOK_SOURCE_LABELS[source as WebhookEventSource] ?? source;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

function formatRelative(iso: string | null): string {
  if (!iso) return 'never';
  const seconds = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

/** datetime-local gives a wall-clock string with no zone; send the matching instant. */
function localInputToIso(value: string): string | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

function prettyPayload(payload: string): string {
  try {
    return JSON.stringify(JSON.parse(payload), null, 2);
  } catch {
    return payload;
  }
}

function buildQuery(filters: Filters, page: number, pageSize: number): WebhookEventQuery {
  return {
    source: filters.source,
    status: filters.status,
    eventType: filters.eventType,
    search: filters.search.trim(),
    from: localInputToIso(filters.from),
    to: localInputToIso(filters.to),
    page: page + 1,
    pageSize,
  };
}

interface EventDetailDialogProps {
  eventId: string | null;
  onClose: () => void;
  onRetried: (event: WebhookEventDetail) => void;
}

function EventDetailDialog({ eventId, onClose, onRetried }: EventDetailDialogProps) {
  const [detail, setDetail] = useState<WebhookEventDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [retryMessage, setRetryMessage] = useState<{ severity: 'success' | 'error'; text: string } | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setRetryMessage(null);
    setCopied(false);
    webhookEventService
      .getEvent(eventId)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load webhook event');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const handleRetry = async () => {
    if (!detail) return;
    setRetrying(true);
    setRetryMessage(null);
    try {
      const result = await webhookEventService.retryEvent(detail.id);
      setDetail(result.event);
      onRetried(result.event);
      setRetryMessage(
        result.succeeded
          ? { severity: 'success', text: `Reprocessed successfully (attempt ${result.event.attemptCount}).` }
          : {
              severity: 'error',
              text: `Reprocessing failed again: ${result.event.errorSummary ?? result.event.status}`,
            },
      );
    } catch (err: unknown) {
      setRetryMessage({ severity: 'error', text: err instanceof Error ? err.message : 'Retry failed' });
    } finally {
      setRetrying(false);
    }
  };

  const handleCopy = async () => {
    if (!detail) return;
    try {
      await navigator.clipboard.writeText(detail.payload);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const open = eventId !== null;
  const title = detail ? detail.eventType : 'Webhook event';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" aria-labelledby="webhook-event-dialog-title">
      <DialogTitle id="webhook-event-dialog-title" sx={{ fontFamily: 'monospace', fontSize: '1rem' }}>
        {title}
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {retryMessage && (
          <Alert severity={retryMessage.severity} sx={{ mb: 2 }}>
            {retryMessage.text}
          </Alert>
        )}
        {loading && !detail && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {detail && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              component="dl"
              sx={{
                m: 0,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'max-content 1fr' },
                columnGap: 3,
                rowGap: 0.75,
                '& dt': { color: 'text.secondary', fontSize: '0.8125rem' },
                '& dd': { m: 0, fontSize: '0.875rem', wordBreak: 'break-all' },
              }}
            >
              <dt>Status</dt>
              <dd>
                <StatusCell status={detail.status} />
              </dd>
              <dt>Source</dt>
              <dd>{sourceLabel(detail.source)}</dd>
              <dt>Event ID</dt>
              <dd style={{ fontFamily: 'monospace' }}>{detail.eventId}</dd>
              <dt>Subject</dt>
              <dd>{detail.subject ?? '—'}</dd>
              <dt>Received</dt>
              <dd>{formatDateTime(detail.receivedAt)}</dd>
              <dt>Processed</dt>
              <dd>
                {formatDateTime(detail.processedAt)}
                {detail.processingDurationMs != null && ` · ${detail.processingDurationMs.toLocaleString()} ms`}
              </dd>
              <dt>Attempts</dt>
              <dd>{detail.attemptCount}</dd>
            </Box>

            {detail.errorMessage && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5, color: 'status.attention.fg' }}>
                  Error
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: 'status.attention.bg',
                    fontSize: '0.75rem',
                    maxHeight: 220,
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {detail.errorMessage}
                </Box>
              </Box>
            )}

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="subtitle2">Raw payload</Typography>
                <Button size="small" startIcon={<ContentCopyIcon fontSize="small" />} onClick={handleCopy}>
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </Box>
              <Box
                component="pre"
                data-testid="webhook-payload"
                sx={{
                  m: 0,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: 'surface.raised',
                  border: '1px solid',
                  borderColor: 'divider',
                  fontSize: '0.75rem',
                  maxHeight: 360,
                  overflow: 'auto',
                }}
              >
                {prettyPayload(detail.payload)}
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {detail?.canRetry && (
          <Tooltip title="Re-run this event through its processor using the stored payload">
            <span>
              <Button
                variant="contained"
                color="primary"
                startIcon={retrying ? <CircularProgress size={16} color="inherit" /> : <ReplayIcon />}
                onClick={handleRetry}
                disabled={retrying}
              >
                {retrying ? 'Retrying…' : 'Retry processing'}
              </Button>
            </span>
          </Tooltip>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function WebhookEvents() {
  const [stats, setStats] = useState<WebhookEventStats | null>(null);
  const [page, setPage] = useState<{ items: WebhookEventSummary[]; totalCount: number } | null>(null);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [searchInput, setSearchInput] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Filters can change while a request is in flight; only the newest response may land.
  const latestRequestRef = useRef(0);

  const fetchAll = useCallback(async () => {
    const requestId = ++latestRequestRef.current;
    setRefreshing(true);
    try {
      const [statsResult, pageResult] = await Promise.allSettled([
        webhookEventService.getStats(24),
        webhookEventService.getEvents(buildQuery(filters, pageIndex, pageSize)),
      ]);
      if (requestId !== latestRequestRef.current) return;
      if (statsResult.status === 'fulfilled') setStats(statsResult.value);
      if (pageResult.status === 'fulfilled') {
        setPage({ items: pageResult.value.items, totalCount: pageResult.value.totalCount });
        setError(null);
      } else {
        const reason = pageResult.reason;
        setError(reason instanceof Error ? reason.message : 'Failed to load webhook events');
      }
    } finally {
      if (requestId === latestRequestRef.current) setRefreshing(false);
    }
  }, [filters, pageIndex, pageSize]);

  // Initial load + reload on filter/page change; quiet auto-refresh while the tab is visible.
  useEffect(() => {
    fetchAll();
    const timer = setInterval(() => {
      if (!document.hidden) fetchAll();
    }, AUTO_REFRESH_MS);
    const onVisibilityChange = () => {
      if (!document.hidden) fetchAll();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [fetchAll]);

  // Debounce free-text search so each keystroke doesn't hit the API.
  useEffect(() => {
    const handle = setTimeout(() => {
      setFilters((prev) => (prev.search === searchInput ? prev : { ...prev, search: searchInput }));
      setPageIndex(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const updateFilter = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPageIndex(0);
  };

  const handleSelectChange = (key: 'source' | 'status' | 'eventType') => (event: SelectChangeEvent<string>) => {
    updateFilter(key, event.target.value as Filters[typeof key]);
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSearchInput('');
    setPageIndex(0);
  };

  const handleRetried = (updated: WebhookEventDetail) => {
    // Reflect the new status in the table without waiting for the next refresh.
    setPage((prev) =>
      prev
        ? {
            ...prev,
            items: prev.items.map((item) =>
              item.id === updated.id || item.eventId === updated.eventId ? { ...item, ...updated } : item,
            ),
          }
        : prev,
    );
    fetchAll();
  };

  const hasFilters = Object.values(filters).some((value) => value !== '');
  const failedTotal = stats?.failedTotal ?? 0;

  return (
    <Box>
      {/* Summary + refresh. Loud when something needs attention, quiet otherwise. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.75, flexWrap: 'wrap' }}>
        <Box aria-live="polite" sx={{ flex: 1, minWidth: 240 }}>
          {stats && failedTotal > 0 ? (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 0.75,
                borderRadius: 1.5,
                bgcolor: 'status.attention.bg',
              }}
            >
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'status.attention.dot', flexShrink: 0 }} />
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'status.attention.fg' }}>
                {failedTotal === 1 ? '1 failed event needs attention' : `${failedTotal} failed events need attention`}
                {stats.failed !== failedTotal && ` · ${stats.failed} in the last ${stats.windowHours}h`}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, py: 0.75 }}>
              {stats && (
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'status.completed.dot', flexShrink: 0 }} />
              )}
              <Typography variant="body2" color="text.secondary">
                {stats
                  ? stats.received === 0
                    ? `No webhooks received in the last ${stats.windowHours} hours`
                    : `${stats.succeeded} of ${stats.received} webhooks in the last ${stats.windowHours}h processed successfully` +
                      (stats.inFlight > 0 ? ` · ${stats.inFlight} still in flight` : '')
                  : 'Loading webhook activity…'}
              </Typography>
            </Box>
          )}
        </Box>
        <Button
          variant="outlined"
          startIcon={refreshing ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
          onClick={fetchAll}
          disabled={refreshing}
        >
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </Button>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        {stats &&
          stats.bySource
            .map((s) => `${sourceLabel(s.source)}: ${s.received} received, last ${formatRelative(s.lastReceivedAt)}`)
            .join(' · ')}
        {stats && ' · '}
        auto-refreshes every 60 seconds
      </Typography>

      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="webhook-source-label">Source</InputLabel>
            <Select
              labelId="webhook-source-label"
              label="Source"
              value={filters.source}
              onChange={handleSelectChange('source')}
              inputProps={{ 'aria-label': 'Source' }}
            >
              <MenuItem value="">All sources</MenuItem>
              {WEBHOOK_EVENT_SOURCES.map((source) => (
                <MenuItem key={source} value={source}>
                  {WEBHOOK_SOURCE_LABELS[source]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="webhook-status-label">Status</InputLabel>
            <Select
              labelId="webhook-status-label"
              label="Status"
              value={filters.status}
              onChange={handleSelectChange('status')}
              inputProps={{ 'aria-label': 'Status' }}
            >
              <MenuItem value="">All statuses</MenuItem>
              {WEBHOOK_EVENT_STATUSES.map((status) => (
                <MenuItem key={status} value={status}>
                  {STATUS_META[status]?.label ?? status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="webhook-type-label">Event type</InputLabel>
            <Select
              labelId="webhook-type-label"
              label="Event type"
              value={filters.eventType}
              onChange={handleSelectChange('eventType')}
              inputProps={{ 'aria-label': 'Event type' }}
            >
              <MenuItem value="">All event types</MenuItem>
              {(stats?.eventTypes ?? []).map((type) => (
                <MenuItem key={type} value={type} sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Search"
            placeholder="Event id, error text or payload"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            sx={{ minWidth: 240, flex: 1 }}
            slotProps={{ htmlInput: { 'aria-label': 'Search' } }}
          />
          <TextField
            size="small"
            label="From"
            type="datetime-local"
            value={filters.from}
            onChange={(event) => updateFilter('from', event.target.value)}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'aria-label': 'From' } }}
          />
          <TextField
            size="small"
            label="To"
            type="datetime-local"
            value={filters.to}
            onChange={(event) => updateFilter('to', event.target.value)}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'aria-label': 'To' } }}
          />
          {hasFilters && (
            <Button size="small" onClick={clearFilters}>
              Clear filters
            </Button>
          )}
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* The ledger */}
      <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>Received</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Event type</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell sx={{ width: 120 }}>Status</TableCell>
              <TableCell align="right" sx={{ width: 80 }}>
                Attempts
              </TableCell>
              <TableCell align="right" sx={{ width: 90 }}>
                Duration
              </TableCell>
              <TableCell>Error</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!page
              ? Array.from({ length: 8 }, (_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 8 }, (_, j) => (
                      <TableCell key={j}>
                        <Skeleton width={j === 3 || j === 7 ? '70%' : 80} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : page.items.length === 0
                ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
                      {hasFilters ? 'No webhook events match these filters.' : 'No webhook events recorded yet.'}
                    </TableCell>
                  </TableRow>
                )
                : page.items.map((event) => (
                    <TableRow
                      key={event.id}
                      hover
                      onClick={() => setSelectedId(event.id)}
                      sx={{ cursor: 'pointer', '&:last-child td': { border: 0 } }}
                      data-testid={`webhook-row-${event.id}`}
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8125rem' }}>
                        <Tooltip title={event.receivedAt} placement="top-start" enterDelay={600}>
                          <span>{formatDateTime(event.receivedAt)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip label={sourceLabel(event.source)} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>
                        {event.eventType}
                      </TableCell>
                      <TableCell
                        sx={{ fontSize: '0.8125rem', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {event.subject ?? <Typography component="span" variant="caption" color="text.disabled">—</Typography>}
                      </TableCell>
                      <TableCell>
                        <StatusCell status={event.status} />
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {event.attemptCount}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                        {event.processingDurationMs != null ? `${event.processingDurationMs.toLocaleString()} ms` : '—'}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: '0.8125rem',
                          color: event.status === 'failed' ? 'status.attention.fg' : 'text.secondary',
                          maxWidth: 320,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {event.errorSummary ?? '—'}
                      </TableCell>
                    </TableRow>
                  ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={page?.totalCount ?? 0}
          page={pageIndex}
          onPageChange={(_, newPage) => setPageIndex(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(event) => {
            setPageSize(Number(event.target.value));
            setPageIndex(0);
          }}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
        />
      </Paper>

      <EventDetailDialog eventId={selectedId} onClose={() => setSelectedId(null)} onRetried={handleRetried} />
    </Box>
  );
}
